import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";

// "Visitas" ahora llega como dos líneas separadas ("Visitas Programadas" / "Visitas
// Realizadas"); se reconocen ambas por prefijo para no depender del nombre exacto.
const esVisitas = (linea) => (linea || "").trim().toLowerCase().startsWith("visitas");

// Ícono "guardar" (mismo trazo que el ícono Save de MUI) para el botón Guardar del modal de
// comentario: SweetAlert2 no renderiza componentes de React, solo HTML/SVG plano.
const ICONO_GUARDAR_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" ' +
  'fill="currentColor" style="vertical-align:middle;margin-right:6px;">' +
  '<path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 ' +
  '3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg>';

// Extrae del label de la semana (p.ej. "01 - 02 de Agosto") el día inicial y final del rango,
// tomando los dos primeros números que aparezcan. Solo se usa para ubicar la semana actual
// dentro del mes/ejercicio que ya se está consultando (no valida el mes por texto).
const parsearRangoDias = (semana) => {
  const numeros = (semana || "").match(/\d{1,2}/g);
  if (!numeros || numeros.length < 2) return null;
  const inicio = Number(numeros[0]);
  const fin = Number(numeros[1]);
  if (Number.isNaN(inicio) || Number.isNaN(fin)) return null;
  return { inicio, fin };
};

// Determina qué semana contiene la fecha de hoy, solo cuando el ejercicio/periodo consultado
// coincide con el año/mes reales (si el usuario está viendo otro periodo, no hay "semana actual").
const obtenerSemanaActualId = (semanas, ejercicio, periodo) => {
  const hoy = new Date();
  const esPeriodoActual = Number(ejercicio) === hoy.getFullYear() && Number(periodo) === hoy.getMonth() + 1;
  if (!esPeriodoActual) return null;
  const diaHoy = hoy.getDate();
  const encontrada = semanas.find((s) => {
    const rango = parsearRangoDias(s.semana);
    return rango && diaHoy >= rango.inicio && diaHoy <= rango.fin;
  });
  return encontrada ? encontrada.idsemana : null;
};

// Arma los ejes (semanas/vendedores/líneas) y el mapa de valores a partir del arreglo plano de
// GET /Listado. Los ejes salen de los datos: si una semana/vendedor/línea no tiene ningún
// registro, no aparece. "Visitas" se separa del resto: no cuenta para Total Cotizaciones y se
// dibuja siempre al final, después de esa columna calculada. Los vendedores quedan ordenados por
// idestado ascendente (y alfabético dentro del mismo estado) para poder agruparlos en el render.
const armarPivot = (datos) => {
  const semanasMap = new Map();
  const vendedoresMap = new Map();
  const lineasMap = new Map();
  const valores = {};

  datos.forEach((row) => {
    if (!semanasMap.has(row.idsemana)) semanasMap.set(row.idsemana, row.semana);
    if (!vendedoresMap.has(row.idvendedor)) {
      vendedoresMap.set(row.idvendedor, { vendedor: row.vendedor, idestado: row.idestado, estado: row.estado });
    }
    if (!lineasMap.has(row.idlinea)) lineasMap.set(row.idlinea, row.linea);
    valores[`${row.idsemana}_${row.idvendedor}_${row.idlinea}`] = row.objetivo;
  });

  const semanas = [...semanasMap.entries()]
    .map(([idsemana, semana]) => ({ idsemana, semana }))
    .sort((a, b) => a.idsemana - b.idsemana);

  const vendedores = [...vendedoresMap.entries()]
    .map(([idvendedor, info]) => ({ idvendedor, ...info }))
    .sort((a, b) => {
      if (a.idestado !== b.idestado) return a.idestado - b.idestado;
      return (a.vendedor || "").localeCompare(b.vendedor || "", "es");
    });

  const lineasTodas = [...lineasMap.entries()].map(([idlinea, linea]) => ({ idlinea, linea }));
  const lineas = [
    ...lineasTodas.filter((l) => !esVisitas(l.linea)),
    ...lineasTodas.filter((l) => esVisitas(l.linea)),
  ];

  return { semanas, vendedores, lineas, valores };
};

// Agrupa vendedores consecutivos (ya vienen ordenados por idestado) para pintar un miniheader
// de estado antes de cada grupo, en vez de repetir la columna Estado en cada renglón.
const agruparPorEstado = (vendedores) => {
  const grupos = [];
  vendedores.forEach((v) => {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.idestado === v.idestado) {
      ultimo.vendedores.push(v);
    } else {
      grupos.push({ idestado: v.idestado, estado: v.estado, vendedores: [v] });
    }
  });
  return grupos;
};

/**
 * ObjetivosSemanalesTabla
 * Grid de captura de objetivos semanales: pivotea el arreglo plano de GET /Listado en
 * semanas (una mini-tabla por semana, apiladas) × vendedores (renglones) × líneas
 * (subcolumnas), con celdas numéricas editables y "Total cotizaciones" recalculado en vivo
 * (suma de las líneas de la semana, excluyendo Visitas). No llama a la API directamente:
 * recibe los datos ya cargados y delega guardar/generar semanas al padre, que es quien conoce
 * ejercicio/periodo y arma el payload real. Al guardar solo se manda el detalle de las celdas
 * cuyo valor cambió respecto a lo que llegó en `datos` (comparación contra `pivot.valores`); las
 * celdas que el usuario no tocó no se reenvían.
 *
 * Los botones "Generar semanas" y "Guardar objetivos" viven en el header de
 * CRM_ObjetivosMainScreen (no aquí): el padre los dispara vía ref (`guardar`/`generarSemanas`,
 * expuestos con useImperativeHandle) porque son quienes conocen el estado de captura actual.
 *
 * Props:
 *   datos                 — array plano de GET /Listado (puede venir vacío); cada fila trae
 *                            también idestado/estado del vendedor, usados para agrupar renglones
 *   cargando              — bool, evita mostrar "Sin objetivos capturados" mientras carga
 *   ejercicio             — number, para el mensaje de confirmación de "Generar semanas"
 *   periodo               — number (1-12), junto con ejercicio se usa para ubicar la "semana
 *                            actual" (la que contiene la fecha de hoy) y abrirla por default
 *   onGuardar             — fn(detalle, comentarios) => Promise; detalle =
 *                            [{idsemana,idvendedor,idlinea,objetivo}]; comentarios = string (puede
 *                            venir vacío, se captura en un modal opcional antes de guardar)
 *   onGenerarSemanas      — fn() => Promise
 *   onEstadoAccionesChange — fn({guardando, generando}) — para que el header refleje disabled/label
 */
export const ObjetivosSemanalesTabla = forwardRef(({ datos, cargando, ejercicio, periodo, onGuardar, onGenerarSemanas, onEstadoAccionesChange }, ref) => {
  const pivot = useMemo(() => armarPivot(Array.isArray(datos) ? datos : []), [datos]);
  const [valores, setValores] = useState(pivot.valores);
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  // Acordeón: una sola semana expandida a la vez (null = todas cerradas). Por default arrancan
  // todas cerradas salvo la semana actual, que se calcula una sola vez al llegar los datos.
  const [semanaExpandida, setSemanaExpandida] = useState(null);
  const defaultAplicado = useRef(false);

  useEffect(() => {
    onEstadoAccionesChange && onEstadoAccionesChange({ guardando, generando });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardando, generando]);

  const toggleSemana = (idsemana) => {
    setSemanaExpandida((prev) => (prev === idsemana ? null : idsemana));
  };

  useEffect(() => {
    setValores(pivot.valores);
  }, [pivot]);

  // Solo la primera vez que llegan semanas se calcula el default (semana actual abierta); si el
  // usuario cambia de periodo o guarda/genera después, se respeta lo que haya dejado expandido.
  useEffect(() => {
    if (defaultAplicado.current || pivot.semanas.length === 0) return;
    defaultAplicado.current = true;
    setSemanaExpandida(obtenerSemanaActualId(pivot.semanas, ejercicio, periodo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pivot]);

  const lineasPrincipales = pivot.lineas.filter((l) => !esVisitas(l.linea));
  const lineasVisitas = pivot.lineas.filter((l) => esVisitas(l.linea));
  const gruposEstado = useMemo(() => agruparPorEstado(pivot.vendedores), [pivot]);
  const totalColumnas = 1 + lineasPrincipales.length + 1 + lineasVisitas.length + 1;

  // Solo dígitos; celda vacía = sin objetivo capturado (no se manda al guardar), no cero.
  const handleChange = (idsemana, idvendedor, idlinea, valor) => {
    const key = `${idsemana}_${idvendedor}_${idlinea}`;
    const raw = valor.replace(/[^0-9]/g, "");
    setValores((prev) => {
      const next = { ...prev };
      if (raw === "") delete next[key];
      else next[key] = Number(raw);
      return next;
    });
  };

  const totalCotizacionesCelda = (idsemana, idvendedor) =>
    lineasPrincipales.reduce(
      (acc, l) => acc + (Number(valores[`${idsemana}_${idvendedor}_${l.idlinea}`]) || 0),
      0
    );

  // Suma "Visitas Programadas" + "Visitas Realizadas" de un vendedor en la semana.
  const totalVisitasCelda = (idsemana, idvendedor) =>
    lineasVisitas.reduce(
      (acc, l) => acc + (Number(valores[`${idsemana}_${idvendedor}_${l.idlinea}`]) || 0),
      0
    );

  const totalLineaSemana = (idsemana, idlinea) =>
    pivot.vendedores.reduce((acc, v) => acc + (Number(valores[`${idsemana}_${v.idvendedor}_${idlinea}`]) || 0), 0);

  const totalCotizacionesSemana = (idsemana) =>
    pivot.vendedores.reduce((acc, v) => acc + totalCotizacionesCelda(idsemana, v.idvendedor), 0);

  const totalVisitasSemana = (idsemana) =>
    lineasVisitas.reduce((acc, l) => acc + totalLineaSemana(idsemana, l.idlinea), 0);

  const totalCotizacionesMes = pivot.semanas.reduce((acc, s) => acc + totalCotizacionesSemana(s.idsemana), 0);
  const totalVisitasMes = pivot.semanas.reduce((acc, s) => acc + totalVisitasSemana(s.idsemana), 0);

  // Compara contra pivot.valores (el valor tal cual llegó del último GET /Listado, antes de
  // cualquier edición) para no reenviar celdas que el usuario no tocó — solo se manda lo que
  // realmente cambió de valor.
  const celdaModificada = (key, valor) => Number(valor) !== Number(pivot.valores[key]);

  const handleGuardar = async () => {
    const detalle = Object.entries(valores)
      .filter(([, valor]) => valor !== undefined && valor !== null && valor !== "")
      .filter(([key, valor]) => celdaModificada(key, valor))
      .map(([key, objetivo]) => {
        const [idsemana, idvendedor, idlinea] = key.split("_").map(Number);
        return { idsemana, idvendedor, idlinea, objetivo: Number(objetivo) };
      });

    if (detalle.length === 0) {
      Swal.fire("OBJETIVOS MENSUALES", "No hay cambios nuevos para guardar", "warning");
      return;
    }

    // Comentario opcional antes de guardar: si el usuario cancela el modal, no se guarda nada.
    // Cancelar a la izquierda / Guardar a la derecha (reverseButtons) y el botón Guardar con el
    // mismo verde e ícono que "Guardar objetivos" en el header (.crmcs-btn-excel).
    const { value: comentarios, isConfirmed } = await Swal.fire({
      title: "Comentario (opcional)",
      input: "textarea",
      inputPlaceholder: "Escribe un comentario sobre esta captura (opcional)...",
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: "Cancelar",
      confirmButtonText: ICONO_GUARDAR_SVG + "Guardar",
      customClass: { confirmButton: "obj-swal-btn-guardar" },
    });
    if (!isConfirmed) return;

    setGuardando(true);
    try {
      await onGuardar(detalle, comentarios || "");
    } finally {
      setGuardando(false);
    }
  };

  // GET que escribe en base de datos: se pide confirmación y se deshabilita el botón mientras
  // está en vuelo, para no ejecutarlo dos veces por accidente.
  const handleGenerarSemanas = async () => {
    const confirmacion = await Swal.fire({
      title: "OBJETIVOS MENSUALES",
      text: `¿Generar las semanas del ejercicio ${ejercicio}? Esta acción normalmente se corre una sola vez por ejercicio.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Generar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacion.isConfirmed) return;

    setGenerando(true);
    try {
      await onGenerarSemanas();
    } finally {
      setGenerando(false);
    }
  };

  useImperativeHandle(ref, () => ({
    guardar: handleGuardar,
    generarSemanas: handleGenerarSemanas,
  }));

  return (
    <div className="obj-semanas">
      {cargando ? (
        <p className="obj-sin-semanas">Cargando objetivos...</p>
      ) : pivot.semanas.length === 0 ? (
        <p className="obj-sin-semanas">Sin objetivos capturados.</p>
      ) : (
        <div className="obj-semanas-lista scroll">
          {pivot.semanas.map((semana) => {
            const expandida = semanaExpandida === semana.idsemana;
            return (
            <div className={`obj-semana-card${expandida ? "" : " contraida"}`} key={semana.idsemana}>
              <button
                type="button"
                className="obj-semana-header"
                onClick={() => toggleSemana(semana.idsemana)}
                aria-expanded={expandida}
              >
                <ExpandMoreIcon className="obj-semana-chevron" />
                <span>{semana.semana}</span>
                <span className="obj-semana-header-total">
                  {totalCotizacionesSemana(semana.idsemana)} cotizaciones · {totalVisitasSemana(semana.idsemana)} visitas
                </span>
              </button>
              {expandida && (
              <div className="obj-semana-tabla-scroll">
              <table className="obj-semana-tabla">
                <thead>
                  <tr>
                    <th>Vendedor</th>
                    {lineasPrincipales.map((l) => (
                      <th key={l.idlinea}>{l.linea}</th>
                    ))}
                    <th className="obj-th-total">Total cotiz.</th>
                    {lineasVisitas.map((l) => (
                      <th key={l.idlinea}>{l.linea}</th>
                    ))}
                    <th className="obj-th-total">Total visitas</th>
                  </tr>
                </thead>
                <tbody>
                  {gruposEstado.map((grupo) => (
                    <React.Fragment key={grupo.idestado}>
                      <tr className="obj-fila-estado">
                        <td colSpan={totalColumnas}>{grupo.estado}</td>
                      </tr>
                      {grupo.vendedores.map((v) => (
                        <tr key={v.idvendedor}>
                          <td className="obj-td-vendedor">{v.vendedor}</td>
                          {lineasPrincipales.map((l) => (
                            <td key={l.idlinea}>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={valores[`${semana.idsemana}_${v.idvendedor}_${l.idlinea}`] ?? "0"}
                                onChange={(e) =>
                                  handleChange(semana.idsemana, v.idvendedor, l.idlinea, e.target.value)
                                }
                              />
                            </td>
                          ))}
                          <td className="obj-td-total">{totalCotizacionesCelda(semana.idsemana, v.idvendedor)}</td>
                          {lineasVisitas.map((l) => (
                            <td key={l.idlinea}>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={valores[`${semana.idsemana}_${v.idvendedor}_${l.idlinea}`] ?? ""}
                                onChange={(e) =>
                                  handleChange(semana.idsemana, v.idvendedor, l.idlinea, e.target.value)
                                }
                              />
                            </td>
                          ))}
                          <td className="obj-td-total">{totalVisitasCelda(semana.idsemana, v.idvendedor)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total semana</td>
                    {lineasPrincipales.map((l) => (
                      <td key={l.idlinea}>{totalLineaSemana(semana.idsemana, l.idlinea)}</td>
                    ))}
                    <td className="obj-td-total">{totalCotizacionesSemana(semana.idsemana)}</td>
                    {lineasVisitas.map((l) => (
                      <td key={l.idlinea}>{totalLineaSemana(semana.idsemana, l.idlinea)}</td>
                    ))}
                    <td className="obj-td-total">{totalVisitasSemana(semana.idsemana)}</td>
                  </tr>
                </tfoot>
              </table>
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {pivot.semanas.length > 0 && (
        <div className="obj-semanas-footer">
          <span className="obj-total-mes">
            Total del mes: <strong>{totalCotizacionesMes} cotizaciones</strong> · <strong>{totalVisitasMes} visitas</strong>
          </span>
        </div>
      )}
    </div>
  );
});

ObjetivosSemanalesTabla.displayName = "ObjetivosSemanalesTabla";
